import { useEffect, useId, useState } from "react";
import { Select, SelectItem } from "@/components/ui/select";
import { BOARD_FACULTY_CODE, catalogApi, type Faculty } from "@/lib/catalog-api.service";
import { useAuth } from "@/global-hooks/useAuth";

interface FacultyFilterProps {
	value?: string;
	onChange: (code: string | undefined) => void;
	className?: string;
}

/**
 * Chọn khoa để thu hẹp phạm vi thống kê.
 *
 * Đứng một mình ở đầu trang thay vì lặp trong từng tab: lựa chọn là của cả
 * trang, và mỗi tab dựng một control riêng thì cùng một giá trị lại hiện ở nhiều
 * chỗ, dễ đọc thành nhiều bộ lọc độc lập.
 *
 * Đây chỉ là thu hẹp, không phải phân quyền. Server quyết định tài khoản được
 * thấy gì qua `faculty_code` của chính nó (xem `middleware/scope-faculty.ts`);
 * tham số gửi lên chỉ được thu hẹp tầm nhìn đó, không bao giờ nới ra. Nên khoá
 * control ở đây là chuyện trình bày cho đúng sự thật, không phải rào chắn.
 */
export function FacultyFilter({ value, onChange, className }: FacultyFilterProps) {
	const id = useId();
	const { user } = useAuth();

	// `Board` không phải một khoa — server quy nó về "không giới hạn", nên ghim
	// control vào nó sẽ vừa vô hiệu hoá bộ lọc vừa nói sai về quyền xem.
	const ownCode = (user as { faculty_code?: string | null } | undefined)?.faculty_code || null;
	const pinnedCode = ownCode === BOARD_FACULTY_CODE ? null : ownCode;

	const [faculties, setFaculties] = useState<Faculty[]>([]);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let cancelled = false;
		catalogApi
			.listFaculties()
			.then((list: Faculty[]) => {
				if (!cancelled) setFaculties(list);
			})
			.catch((err: unknown) => {
				console.error("Failed to load faculties:", err);
				// Ghi nhận chứ không hành động: tải hỏng danh sách khoa không được phép
				// xoá khoa đang chọn, vì như vậy là âm thầm nới rộng phạm vi trong lần
				// gọi API kế tiếp.
				if (!cancelled) setFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const pinnedFaculty = faculties.find((f) => f.code === pinnedCode);
	const pinnedName = pinnedFaculty?.name ?? pinnedCode;
	// Tài khoản gắn với một khoa mà catalog hiện không còn liệt kê: khoa bị ngừng
	// hoạt động sau khi gán, và việc ngừng đó không thu hồi quyền đã gán.
	const pinnedIsRetired = !!pinnedCode && !pinnedFaculty && faculties.length > 0;
	// Khoa như vậy vẫn phải là một lựa chọn, nếu không select hiện rỗng và đọc
	// thành "tất cả khoa".
	const khoaOptions = pinnedCode
		? [{ code: pinnedCode, name: pinnedName ?? pinnedCode }]
		: faculties
				.filter((f) => f.code !== BOARD_FACULTY_CODE)
				.map((f) => ({ code: f.code, name: f.name }));

	return (
		<div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
			<Select
				id={id}
				// Nhãn nằm ở `aria-label` chứ không phải thẻ <label> phía trên: control
				// này sống trong hàng nút của header, và một nhãn xếp trên sẽ đội hàng
				// đó cao lên — xem quy tắc 4 và 5 trong CLAUDE.md.
				aria-label="Khoa"
				value={pinnedCode ?? value ?? ""}
				disabled={!!pinnedCode || failed}
				onChange={(e) => onChange(e.target.value || undefined)}
				// h-8 để bằng các nút `size="sm"` cùng hàng. Không trộn chiều cao.
				className="h-8 w-44 text-xs"
			>
				{!pinnedCode && <SelectItem value="">Tất cả khoa</SelectItem>}
				{khoaOptions.map((khoa) => (
					<SelectItem key={khoa.code} value={khoa.code}>
						{khoa.name}
					</SelectItem>
				))}
			</Select>
			{/* Không có dòng "Bạn chỉ xem được dữ liệu của X": select bị khoá và đang
			    hiển thị đúng tên khoa đó, nên câu ấy chỉ lặp lại thứ đã thấy.
			    Hai thông báo dưới đây thì khác — chúng nói điều mà nhìn vào select
			    không suy ra được. */}
			{pinnedIsRetired && (
				<span className="text-xs text-amber-600 dark:text-amber-400">
					Khoa này đã ngừng hoạt động — bạn vẫn xem được dữ liệu của khoa.
				</span>
			)}
			{failed && !pinnedCode && (
				<span className="text-xs text-red-600 dark:text-red-400">
					Không tải được danh sách khoa. Vui lòng tải lại trang.
				</span>
			)}
		</div>
	);
}
